import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiChevronDown } from 'react-icons/fi';

const DropdownSelectQuestion = ({ 
  question, 
  onAnswer, 
  currentAnswer = null,
  isSubmitted = false,
  timeSpent = 0 
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});

  // Initialize selected answers from current answer or empty state
  useEffect(() => {
    if (currentAnswer !== null && currentAnswer !== undefined) {
      setSelectedAnswers(currentAnswer);
    } else {
      setSelectedAnswers({});
    }
  }, [currentAnswer]);

  // Initialize open dropdowns state
  useEffect(() => {
    if (question.options && question.options.length > 0) {
      const initialOpenState = {};
      question.options.forEach((option, index) => {
        initialOpenState[index] = false;
      });
      setOpenDropdowns(initialOpenState);
    }
  }, [question.options]);

  const handleAnswerSelect = (dropdownIndex, answer) => {
    if (isSubmitted) return; // Prevent changes after submission
    
    setSelectedAnswers(prev => ({
      ...prev,
      [dropdownIndex]: answer
    }));
  };

  const toggleDropdown = (dropdownIndex) => {
    if (isSubmitted) return; // Prevent changes after submission
    
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownIndex]: !prev[dropdownIndex]
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Check if all dropdowns have been answered
    const allAnswered = question.options && question.options.every((_, index) => 
      selectedAnswers[index] !== undefined && selectedAnswers[index] !== null
    );

    if (!allAnswered) {
      alert('Please select an answer for all dropdowns before submitting.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onAnswer({
        questionId: question.id,
        selectedAnswers,
        type: 'DROPDOWN_SELECT',
        timeSpent
      });
      
      setShowFeedback(true);
    } catch (error) {
      console.error('Error submitting dropdown select answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAnswerStatus = (dropdownIndex, answer) => {
    if (!showFeedback || !currentAnswer) return null;
    
    const currentAnswerForDropdown = currentAnswer[dropdownIndex];
    const selectedAnswerForDropdown = selectedAnswers[dropdownIndex];
    
    if (answer === currentAnswerForDropdown) {
      return 'correct';
    } else if (answer === selectedAnswerForDropdown) {
      return 'incorrect';
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'correct': return 'border-green-500 bg-green-50';
      case 'incorrect': return 'border-red-500 bg-red-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct': return <FiCheckCircle className="text-green-600 text-lg" />;
      case 'incorrect': return <FiCheckCircle className="text-red-600 text-lg" />;
      default: return null;
    }
  };

  // Handle case where there are no options yet
  if (!question.options || question.options.length === 0) {
    return (
      <div className="dropdown-select-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
        <div className="mb-6">
          {/* Question Text */}
          <div className="text-gray-900 text-base font-bold leading-relaxed mb-4">
            {question.text}
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-800">
              <FiCheckCircle className="text-lg" />
              <span className="font-medium">Question Preview</span>
            </div>
            <p className="text-blue-700 mt-2">
              This is a dropdown select question. The question text is displayed above, but the dropdown options are not yet configured. 
              When fully configured, you'll see multiple dropdowns with various options to select from.
            </p>
            <div className="mt-3 p-3 bg-white rounded border">
              <p className="text-sm text-gray-600">
                <strong>Question:</strong> {question.text}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dropdown-select-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
      {/* Question Text */}
      <div className="mb-6">
        <div className="text-gray-900 text-base font-bold leading-relaxed">
          {question.text}
        </div>
      </div>

      {/* Dropdown Options */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Select answers for each dropdown:</h4>
        
        <div className="space-y-4">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswers[index] !== undefined && selectedAnswers[index] !== null;
            const isOpen = openDropdowns[index];
            
            return (
              <div key={option.id || index} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-gray-700 font-medium">
                    Dropdown {index + 1}:
                  </label>
                  <span className="text-sm text-gray-500">
                    {isSelected ? 'Answered' : 'Not answered'}
                  </span>
                </div>
                
                {/* Dropdown Button */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleDropdown(index)}
                    disabled={isSubmitted}
                    className={`w-full flex items-center justify-between p-3 border-2 rounded-lg transition-all duration-200 ${
                      isSelected 
                        ? 'border-blue-600 bg-blue-50' 
                        : 'border-gray-300 bg-white hover:border-blue-600 hover:bg-blue-50'
                    } ${isSubmitted ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                  >
                    <span className={`${isSelected ? 'text-blue-800' : 'text-gray-500'}`}>
                      {isSelected ? selectedAnswers[index] : `Select an option for dropdown ${index + 1}`}
                    </span>
                    <FiChevronDown 
                      className={`text-gray-400 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`} 
                    />
                  </button>
                  
                  {/* Dropdown Options */}
                  {isOpen && !isSubmitted && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                      <div className="py-1">
                        {option.text.split(',').map((text, optionIndex) => {
                          const trimmedText = text.trim();
                          const status = getAnswerStatus(index, trimmedText);
                          
                          return (
                            <button
                              key={optionIndex}
                              onClick={() => {
                                handleAnswerSelect(index, trimmedText);
                                toggleDropdown(index);
                              }}
                              className={`w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors ${
                                status ? getStatusColor(status) : 'hover:bg-blue-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-gray-800">{trimmedText}</span>
                                {status && getStatusIcon(status)}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Selected Answer Display */}
                {isSelected && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-800 font-medium">
                        Selected: {selectedAnswers[index]}
                      </span>
                      {showFeedback && currentAnswer && (
                        <span className={`text-sm font-medium ${
                          selectedAnswers[index] === currentAnswer[index] 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          {selectedAnswers[index] === currentAnswer[index] ? 'Correct' : 'Incorrect'}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>



      {/* Feedback */}
      {showFeedback && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h5 className="font-medium text-gray-800 mb-2">Answer Submitted</h5>
          <p className="text-sm text-gray-600">
            Your answer has been recorded. You can review it in the question navigator.
          </p>
        </div>
      )}
    </div>
  );
};

export default DropdownSelectQuestion; 